import React, { useState } from 'react';
import { captureError } from '@/utils/errorHandler';

export default function RequestFeatureModal({ 
  isOpen, 
  onClose, 
  onSubmit, 
  isSubmitting = false,
  appDescription = '',
  existingFeatures = [],
}) {
  // Unified pending features list
  const [pendingFeatures, setPendingFeatures] = useState([]);
  
  // Manual entry form state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualName, setManualName] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  
  // AI suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // Can show AI suggestions if we have app context
  const canSuggest = appDescription && appDescription.trim().length >= 20;

  // Check if manual form has valid input
  const hasValidManualInput = manualName.trim().length > 0;
  
  // Check if we have anything to submit
  const canSubmit = pendingFeatures.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    // Submit all pending features
    const featuresToSubmit = pendingFeatures.map(f => ({
      name: f.name,
      description: f.description || '',
    }));

    await onSubmit(featuresToSubmit);
    
    // Reset form
    resetForm();
  };

  const resetForm = () => {
    setPendingFeatures([]);
    setShowManualForm(false);
    setManualName('');
    setManualDescription('');
    setSuggestions([]);
    setShowSuggestions(false);
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFetchSuggestions = async () => {
    setLoadingSuggestions(true);
    setShowSuggestions(true);
    try {
      // Include both existing features and pending features to avoid duplicates
      const allCurrentFeatures = [
        ...existingFeatures.map(f => ({ name: f, description: '' })),
        ...pendingFeatures.map(f => ({ name: f.name, description: f.description })),
      ];
      
      const response = await fetch('/api/landing/suggest-features', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appDescription,
          existingFeatures: allCurrentFeatures,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to get suggestions');
      
      const data = await response.json();
      setSuggestions(data.features || []);
    } catch (error) {
      captureError(error, {
        action: 'fetchFeatureSuggestions',
        component: 'RequestFeatureModal',
        extra: { appDescription, existingFeaturesCount: existingFeatures.length },
      });
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleAddSuggestion = (feature, index) => {
    // Add to pending list
    setPendingFeatures(prev => [...prev, {
      id: `ai-${Date.now()}-${index}`,
      name: feature.name,
      description: feature.description,
      source: 'ai',
    }]);
    
    // Remove from suggestions
    setSuggestions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddManualFeature = () => {
    if (!hasValidManualInput) return;
    
    setPendingFeatures(prev => [...prev, {
      id: `manual-${Date.now()}`,
      name: manualName.trim(),
      description: manualDescription.trim(),
      source: 'manual',
    }]);
    
    // Reset and close form
    setManualName('');
    setManualDescription('');
    setShowManualForm(false);
  };

  const handleRemoveFeature = (featureId) => {
    setPendingFeatures(prev => prev.filter(f => f.id !== featureId));
  };

  const handleAddAllSuggestions = () => {
    const newFeatures = suggestions.map((feature, index) => ({
      id: `ai-${Date.now()}-${index}`,
      name: feature.name,
      description: feature.description,
      source: 'ai',
    }));
    setPendingFeatures(prev => [...prev, ...newFeatures]);
    setSuggestions([]);
  };

  const handleStartEdit = (feature) => {
    setEditingId(feature.id);
    setEditName(feature.name);
    setEditDescription(feature.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
  };

  const handleSaveEdit = () => {
    if (!editName.trim()) return;
    
    setPendingFeatures(prev => prev.map(f => 
      f.id === editingId 
        ? { ...f, name: editName.trim(), description: editDescription.trim() }
        : f
    ));
    handleCancelEdit();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">💡</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Request Features</h2>
            <p className="text-sm text-gray-600">Build a list of features you'd like to add</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* AI Suggestions Button */}
          {canSuggest && !showSuggestions && (
            <div className="mb-4">
              <button
                type="button"
                onClick={handleFetchSuggestions}
                disabled={loadingSuggestions}
                className="w-full flex items-center justify-center gap-2 p-3 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                ✨ Suggest Features with AI
              </button>
            </div>
          )}

          {/* AI Suggestions Panel */}
          {showSuggestions && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-amber-800 text-sm flex items-center gap-2">
                  <span>✨</span> AI Suggestions
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowSuggestions(false);
                    setSuggestions([]);
                  }}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {loadingSuggestions ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                  <span className="ml-2 text-sm text-amber-700">Generating ideas...</span>
                </div>
              ) : suggestions.length === 0 ? (
                <p className="text-sm text-amber-700 text-center py-2">
                  No more suggestions available. Try adding features manually!
                </p>
              ) : (
                <>
                  <p className="text-xs text-amber-600 mb-2">Click a suggestion to add it to your list</p>
                  
                  {/* Add All Button */}
                  <button
                    type="button"
                    onClick={handleAddAllSuggestions}
                    className="mb-2 text-xs text-amber-700 hover:text-amber-900 font-medium"
                  >
                    + Add all {suggestions.length} suggestions
                  </button>
                  
                  {/* Suggestions List */}
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {suggestions.map((feature, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleAddSuggestion(feature, index)}
                        className="w-full text-left p-3 bg-white hover:bg-amber-100 border border-amber-200 rounded-lg transition-all group"
                      >
                        <div className="flex items-start gap-2">
                          <span className="text-amber-500 group-hover:text-amber-700 mt-0.5">+</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm">{feature.name}</p>
                            <p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Pending Features List */}
          {pendingFeatures.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <h4 className="font-medium text-gray-700 text-sm">
                  Features to request ({pendingFeatures.length})
                </h4>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {pendingFeatures.map((feature) => (
                  <div
                    key={feature.id}
                    className={`p-3 rounded-lg ${
                      editingId === feature.id 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'bg-green-50 border border-green-200'
                    }`}
                  >
                    {editingId === feature.id ? (
                      // Edit mode
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Feature name"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          autoFocus
                        />
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          placeholder="Details (optional)"
                          rows={2}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleSaveEdit}
                            disabled={!editName.trim()}
                            className="flex-1 px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="flex-1 px-2 py-1 text-xs font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Display mode
                      <div className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">✓</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900 text-sm">{feature.name}</p>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              feature.source === 'ai' 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {feature.source === 'ai' ? '✨ AI' : '✏️ Manual'}
                            </span>
                          </div>
                          {feature.description && (
                            <p className="text-xs text-gray-600 mt-0.5">{feature.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleStartEdit(feature)}
                            className="text-gray-400 hover:text-blue-500 transition-colors p-0.5"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveFeature(feature.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-0.5"
                            title="Remove"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Manual Feature Section */}
          <div className="mb-4">
            {!showManualForm ? (
              <button
                type="button"
                onClick={() => setShowManualForm(true)}
                className="w-full flex items-center justify-center gap-2 p-3 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Feature Manually
              </button>
            ) : (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-blue-800 text-sm flex items-center gap-2">
                    <span>✏️</span> Add Your Own Feature
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowManualForm(false);
                      setManualName('');
                      setManualDescription('');
                    }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label htmlFor="manual-name" className="block text-xs font-medium text-gray-700 mb-1">
                      Feature name <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="manual-name"
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      placeholder="e.g., Export data to PDF"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="manual-description" className="block text-xs font-medium text-gray-700 mb-1">
                      Details (optional)
                    </label>
                    <textarea
                      id="manual-description"
                      value={manualDescription}
                      onChange={(e) => setManualDescription(e.target.value)}
                      placeholder="Describe how you'd like this feature to work..."
                      rows={2}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleAddManualFeature}
                    disabled={!hasValidManualInput}
                    className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    + Add to List
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Empty State */}
          {pendingFeatures.length === 0 && !showSuggestions && !showManualForm && (
            <div className="text-center py-6 text-gray-500">
              <p className="text-sm">Use AI suggestions or add features manually</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  <span>💡</span>
                  {canSubmit 
                    ? `Submit ${pendingFeatures.length} Feature${pendingFeatures.length !== 1 ? 's' : ''}`
                    : 'Add Features First'
                  }
                </>
              )}
            </button>
          </div>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          Features will be added to your app's requirements.
        </p>
      </div>
    </div>
  );
}
